// [S-487 / S-488] Home — chat-first landing (Home v2 + Chat Wireframe). The
// Contract Assistant is the whole page: a welcome composer + suggested prompts
// that open into a live conversation. Chat HISTORY lives in the global sidebar
// (the "Chat" section), not here — clicking a chat there routes to
// /home?chat=<id>, which this screen reads to open that conversation.
//
// Chat plumbing is REUSED from ChatPane so file attachment, tool calling,
// streaming/timing and the layer-tagged (L1 blue / L2 purple) reference cards
// all carry over unchanged.
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useSession } from "../auth/AuthProvider";
import { Button, useIsMobile } from "../components";
import { useSidebarCollapse } from "../shell/AppShell";
import { RAIL_DEFAULT_FRACTION } from "./railSplit";
import { useSplitDrag } from "./useSplitDrag";
import {
  useChatSessions,
  useCreateChatSession,
  type ActiveRecord,
  type ChatSessionDTO,
  type RecordRef,
} from "../data/chatQuery";
import { ChatPane, TenantPrompt } from "./agents/ChatPane";
import {
  RecordDrawerProvider,
  RecordDrawerRail,
  useCloseRecordOnChange,
  useRecordDrawer,
} from "./agents/RecordDrawer";

// [rail epic] The chat/document split. The document rail ALWAYS opens at 60%
// of the content area (chat 40%); the user can drag it for the current view,
// and double-clicking the handle snaps back. The width is a FRACTION of the
// container, never saved as pixels: a pixel width saved on a wide monitor
// (2176px was seen) came back on a laptop and squashed the document. Nothing is
// persisted across loads for the same reason.
const LEGACY_RAIL_WIDTH_KEY = "cs.homeRailWidth";

// Suggested opening prompts. The dot color signals which source the question
// leans on (cert = certified templates / L1, stud = precedent / L2) — the
// agent still routes sourcing itself; the dot is just a hint.
const SUGGESTED: ReadonlyArray<{
  source: "cert" | "stud";
  q: string;
  label: string;
}> = [
  {
    source: "cert",
    q: "Review a Word draft against the certified template.",
    label: "Review a Word draft against the certified template",
  },
  {
    source: "cert",
    q: "Find the certified template for a SAG-AFTRA theatrical talent deal.",
    label: "Find the certified template for a SAG-AFTRA theatrical deal",
  },
  {
    source: "stud",
    q: "Search precedent for backend participation on A-list talent.",
    label: "Search precedent for backend participation, A-list talent",
  },
];

export function Home(): JSX.Element {
  const session = useSession();
  const firstName = session?.displayName?.split(" ")[0] ?? "there";
  // Chat is per-studio (precedent search is tenant-scoped). Tenant users get
  // their studio from the session; a platform_admin with no active tenant must
  // name one — same rule as StudioAgents.
  const [tenantInput, setTenantInput] = useState("");
  const studioId =
    session?.studioId ??
    (tenantInput.trim().length > 0 ? tenantInput.trim() : null);
  // Scope chat to the ACTIVE tenant id directly — it tracks an organization switch
  // reliably (it's what request headers carry, so drafts/precedent follow it).
  // session.studioId can lag the switch (its derivation waits on the membership
  // lookup), which left chat history pinned to one tenant. Fall back to the
  // login/typed studio when there's no active-tenant id (platform_admin).
  const tenantId =
    session?.activeTenantId ??
    (studioId !== null ? `tenant-${studioId}` : undefined);

  // [mobile-rail-fix] A persistent side-by-side rail doesn't fit a phone
  // screen — there's no room to show both the conversation and an opened
  // document at once. Below the mobile breakpoint, fall back to the
  // RecordDrawer's existing "overlay" mode (a full-screen slide-over that
  // already covers the whole viewport on a narrow screen, S-413) instead of
  // trying to force the two-column rail into a stacked column.
  const isMobile = useIsMobile();
  return (
    <RecordDrawerProvider mode={isMobile ? "overlay" : "rail"}>
      <HomeRailLayout isMobile={isMobile}>
        {tenantId === undefined ? (
          <div className="home2__nudge">
            <h1 className="h2-greet">
              Welcome back, {firstName}.
              <br />
              <em>Pick a studio to get started.</em>
            </h1>
            <TenantPrompt value={tenantInput} onChange={setTenantInput} />
          </div>
        ) : (
          <ChatHome firstName={firstName} tenantId={tenantId} />
        )}
      </HomeRailLayout>
    </RecordDrawerProvider>
  );
}

/** [rail epic] Chat + the persistent record rail, side by side. The rail
 *  column collapses to nothing when no record is open — <RecordDrawerRail/>
 *  itself renders null in that case, so no separate "is anything open" state
 *  is needed for THAT — but the wrapper that sizes/drags it does need to
 *  know, so it can (a) reclaim the app shell's left nav width while open and
 *  (b) not occupy a fixed-width column when there's nothing to show.
 *
 *  [mobile-rail-fix] `isMobile` gates all of that off, not just the CSS: the
 *  Provider is already rendering the record in "overlay" mode by itself (see
 *  Home() above), so this layout must NOT also mount <RecordDrawerRail/> —
 *  it would show the SAME open record twice, once as the overlay and once
 *  stacked in this column. */
function HomeRailLayout({
  children,
  isMobile,
}: {
  children: ReactNode;
  isMobile: boolean;
}): JSX.Element {
  const { ref } = useRecordDrawer();
  const isOpen = !isMobile && ref !== null;

  // Auto-collapse the app shell's sidebar while the rail is open, so the
  // document gets the width instead of sitting behind a nav column it
  // doesn't need right now — restore whatever the user had once the rail
  // closes. A ref (not state) remembers the prior value so a manual toggle
  // by the user mid-session isn't fought by this effect re-running for
  // unrelated reasons.
  const { collapsed, setCollapsed } = useSidebarCollapse();
  const wasCollapsedRef = useRef(collapsed);
  useEffect(() => {
    if (isOpen) {
      wasCollapsedRef.current = collapsed;
      if (!collapsed) setCollapsed(true);
    } else {
      setCollapsed(wasCollapsedRef.current);
    }
    // Reacts ONLY to the rail opening/closing — deliberately not to
    // `collapsed`/`setCollapsed` themselves changing identity/value.
  }, [isOpen]);

  // Split width: a fraction of the container (RAIL_DEFAULT_FRACTION), shared with
  // the draft workspace via useSplitDrag so both split the same way and can never
  // leave either side below its minimum.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { sideWidth: railWidth, handleProps } = useSplitDrag({
    containerRef,
    defaultFraction: RAIL_DEFAULT_FRACTION,
  });

  useEffect(() => {
    // The old pixel-width setting is dead; drop it so it can't linger.
    try {
      localStorage.removeItem(LEGACY_RAIL_WIDTH_KEY);
    } catch {
      /* storage unavailable: nothing to clean up */
    }
  }, []);

  return (
    <div className="home2" ref={containerRef}>
      <div className="home2__body">{children}</div>
      {isOpen && (
        <div
          className="home2__rail-wrap"
          style={railWidth !== undefined ? { width: railWidth } : undefined}
        >
          <div
            className="home2__rail-handle"
            {...handleProps}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize panel (double-click to reset to 40/60)"
            title="Drag to resize · double-click to reset"
          />
          <RecordDrawerRail />
        </div>
      )}
    </div>
  );
}

// [rail epic] Home's rail can hold a precedent/template/draft/attachment/
// chat_message preview open (RecordRef has 5 types); only the first three are
// something the chat tools can read back by id (see ActiveRecordType), so an
// attachment/chat_message preview leaves the conversation's activeRecord
// alone rather than sending a type the server would reject.
function toActiveRecord(ref: RecordRef | null): ActiveRecord | null {
  if (ref === null) return null;
  if (ref.type !== "draft" && ref.type !== "template" && ref.type !== "precedent") {
    return null;
  }
  return { type: ref.type, id: ref.id };
}

function ChatHome({
  firstName,
  tenantId,
}: {
  firstName: string;
  tenantId: string;
}): JSX.Element {
  // The active conversation is driven by the URL (?chat=<id>) so the sidebar's
  // Chat section can open any conversation by navigating, and reload/deep-link
  // restores it. No param → the welcome composer.
  const [params, setParams] = useSearchParams();
  const activeId = params.get("chat");
  const { ref: openRef } = useRecordDrawer();
  // [rail fix, found live 2026-08-14] The rail's state lives in the provider,
  // which outlives any one conversation — so opening an attachment here and
  // then picking a different chat in the sidebar left that attachment sitting
  // open beside a conversation it has nothing to do with. A conversation switch
  // closes the panel; the new chat opens whatever IT references.
  useCloseRecordOnChange(activeId);

  const sessions = useChatSessions({ agentKind: "assistant", tenantId });
  const create = useCreateChatSession();
  // The opening message for a just-created chat, keyed by its session id. ChatPane
  // auto-sends it once into the empty conversation; it's ignored for any other
  // (e.g. reopened-from-sidebar) session.
  const [pendingInitial, setPendingInitial] = useState<{
    id: string;
    text: string;
  } | null>(null);
  const [composer, setComposer] = useState("");

  const startChat = useCallback(
    (text?: string) => {
      if (create.isPending) return;
      const trimmed = (text ?? "").trim();
      create.mutate(
        {
          agentKind: "assistant",
          tenantId,
          ...(trimmed.length > 0 ? { title: trimmed.slice(0, 60) } : {}),
        },
        {
          onSuccess: (s) => {
            setPendingInitial(
              trimmed.length > 0 ? { id: s.id, text: trimmed } : null
            );
            setComposer("");
            setParams({ chat: s.id });
          },
        }
      );
    },
    [create, tenantId, setParams]
  );

  const openChat = useCallback(
    (id: string) => {
      setParams({ chat: id });
    },
    [setParams]
  );

  if (activeId === null) {
    const latest: ChatSessionDTO | null = (sessions.data ?? [])[0] ?? null;
    return (
      <Welcome
        firstName={firstName}
        composer={composer}
        setComposer={setComposer}
        onSend={startChat}
        starting={create.isPending}
        latest={latest}
        onResume={openChat}
      />
    );
  }

  // Pass the opening message only for the chat we just created (auto-sent once
  // into the empty conversation); reopened chats load their history normally.
  const initialMessage =
    pendingInitial !== null && pendingInitial.id === activeId
      ? pendingInitial.text
      : undefined;
  return (
    <div className="home2__convo">
      <ChatPane
        key={activeId}
        sessionId={activeId}
        tenantId={tenantId}
        openRecordRef={toActiveRecord(openRef)}
        {...(initialMessage !== undefined ? { initialMessage } : {})}
      />
    </div>
  );
}

function Welcome({
  firstName,
  composer,
  setComposer,
  onSend,
  starting,
  latest,
  onResume,
}: {
  firstName: string;
  composer: string;
  setComposer: (v: string) => void;
  onSend: (text?: string) => void;
  starting: boolean;
  latest: ChatSessionDTO | null;
  onResume: (id: string) => void;
}): JSX.Element {
  const canSend = composer.trim().length > 0 && !starting;
  return (
    <div className="h2-stage">
      <div className="h2-welcome">
        {/* The assistant opens the new chat itself — a real, client-rendered
            greeting (honest: the Contract Assistant genuinely greets this way;
            it is NOT backend data and NOT a fake-working control). Rendered as
            an assistant message bubble so a fresh chat reads like the assistant
            started it. Composer + suggested prompts + resume sit below. */}
        <div
          className="h2-hello"
          role="status"
          aria-label="Contract Assistant greeting"
        >
          <span className="h2-hello__avatar" aria-hidden="true">
            CS
          </span>
          <div className="h2-hello__body">
            <span className="h2-hello__who">Contract Assistant</span>
            <div className="h2-hello__bubble">
              Hi {firstName} — I&rsquo;m your Contract Assistant. I can review a
              draft against your certified templates, search your studio&rsquo;s
              precedent, find a certified template, or answer a deal question.
              What are you working on?
            </div>
          </div>
        </div>

        <div className="h2-composer">
          <textarea
            className="h2-composer__input"
            rows={1}
            placeholder="Describe what you're working on, or ask a question…"
            value={composer}
            onChange={(e) => setComposer(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (canSend) onSend(composer);
              }
            }}
            disabled={starting}
          />
          <div className="h2-composer__bar">
            <Button
              variant="bare"
              className="h2-send"
              onClick={() => canSend && onSend(composer)}
              disabled={!canSend}
            >
              {starting ? "Starting…" : "Ask"}
              <ArrowIcon />
            </Button>
          </div>
        </div>

        <div className="h2-starters-lbl">Suggested prompts</div>
        <div className="h2-prompts">
          {SUGGESTED.map((p) => (
            <Button
              key={p.q}
              variant="bare"
              className="h2-prompt"
              onClick={() => onSend(p.q)}
              disabled={starting}
            >
              <span className={`dot dot--${p.source}`} />
              {p.label}
              <span className="arrow">→</span>
            </Button>
          ))}
        </div>

        {latest !== null && (
          <Button
            variant="bare"
            className="h2-resume"
            onClick={() => onResume(latest.id)}
          >
            <span className="h2-resume__edge" />
            <div className="h2-resume__body">
              <div className="h2-resume__lbl">Pick up where you left off</div>
              <div className="h2-resume__title">{latest.title}</div>
            </div>
            <span className="h2-resume__meta">last chat</span>
            <span className="h2-resume__go">→</span>
          </Button>
        )}
      </div>
    </div>
  );
}

function ArrowIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </svg>
  );
}
